import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import * as RA from 'ramda-adjunct';
import {TreeComponent, TreeNode} from '@ali-hm/angular-tree-component';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {ItemNode} from '@emm/core/util/angular-tree-component.util';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {UserService} from '@emm/core/services/user.service';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-generic-items-tree',
    styleUrls: ['./generic-items-tree.component.scss'],
    templateUrl: './generic-items-tree.component.html',
    standalone: false
})
export class GenericItemsTreeComponent implements OnInit, OnChanges {

    @Input() contextMenuActions: ContextMenuAction<any>[];
    @Input() disableContextActions: boolean;
    @Input() disableDefaultOnMove: boolean;
    @Input() items: ItemNode[];
    @Input() options: any;
    @Input() readonly: boolean;
    @Input() nodesToExpand: string[];
    @Input() searchedTag: string;
    @Input() showLockedIndicator = false;
    @Input() lockedPredicate: (node: TreeNode | any) => boolean;
    @Input() isMmsAuthorOnly = false;

    @Output() moveItem = new EventEmitter<{
        fromPath: string;
        toPath: string;
        onSuccess: () => void;
    }>();

    @ViewChild(TreeComponent) menuTree: TreeComponent;

    showTags$: Observable<boolean>;

    constructor(private sharedFacadeService: SharedFacadeService, private userService: UserService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.nodesToExpand && this.menuTree?.treeModel?.nodes) {
            this.nodesToExpand?.forEach(nodeId => {
                this.menuTree.treeModel.getNodeById(nodeId).expand();
            });
        }
        if (changes['items'] && RA.isEmptyArray(this.items)) {
            if (this.menuTree) {
                this.menuTree.treeModel?.collapseAll();
                this.menuTree.treeModel?.setState({ expandedNodeIds: {} });
            }
        }
    }

    ngOnInit(): void {
        this.showTags$ = this.sharedFacadeService.showTags$.pipe(
            map(showTags => showTags === 'true')
        );
    }

    onMoveNode(event) {
        if (!this.disableDefaultOnMove) {

            const dragNode = event.node;
            const targetParent = event.to.parent;
            const fromParent = event.from.parent;
            const treeModel = this.menuTree.treeModel;
            const parentNode: TreeNode = treeModel.getNodeById(targetParent.id);
            const fromNode: TreeNode = treeModel.getNodeById(fromParent.id);
            const fromPath = dragNode?.data?.path;
            const toPath = (targetParent?.data ? `${targetParent?.data?.path}` : '') + `/items/${event?.to?.index}`;

            const onSuccess = () => {
                if (parentNode) {
                    parentNode.expand();
                    parentNode.loadNodeChildren().then();
                }

                if (fromNode) {
                    fromNode.expand();
                    fromNode.loadNodeChildren().then();
                }
            };

            this.moveItem.emit({ fromPath, toPath, onSuccess });
        }
    }

    get isEmpty(): boolean {
        if (this.items === null) {
            return false;
        }
        return this.items ? this.items.length === 0 : true;
    }

    isLocked(node: TreeNode | any): boolean {
        if (!this.showLockedIndicator) { return false; }
        if (!node) { return false; }
        if (this.lockedPredicate) {
            try { return !!this.lockedPredicate(node); } catch { return false; }
        }
        const data = node.data;
        return !!(data?.locked ?? data?.data?.locked);
    }

}
